import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { OnChange } from 'react-final-form-listeners';
import styled from '@emotion/styled';

import { Form, Grid, FlexBaseCol, FlexRow, Icon } from '@contently/toolkit';

import SkillField, { useFetchingFieldSkills } from './SkillField';
import SelectField from './SelectField';
import InputField from './InputField';
import HiddenField from './HiddenField';

import useFetchingField from '../utils/hooks/useFetchingField';
import useFetchingForm from '../utils/hooks/useFetchingForm';
import useLocalStorage from '../utils/hooks/useLocalStorage';
import validation, { isRequired, isEmail } from '../utils/validation';

const StyledButton = styled(Form.Button)`
  border: 0;
  text-transform: none;
  background: #e8f7fa;
  ${Icon} {
    font-size: 1.25rem;
    margin-left: 0.5em;
  }
`;

const parseTopics = res => {
  return res.data.map(topic => {
    return { value: topic.name, label: topic.name };
  });
};

const splitNames = name => {
  const splitNamesArray = name.split(' ');
  const firstName = splitNamesArray.shift();
  const lastName = splitNamesArray.join(' ');
  return [firstName, lastName];
};

const IntakeFormRender = props => {
  const [topics, topicsFetchingState] = useFetchingField('/topics?visible=true', [], parseTopics);
  const [skills, skillsFetchingState] = useFetchingFieldSkills();
  const [, formErrored] = useFetchingForm(topicsFetchingState, skillsFetchingState);
  const directCustomer = useLocalStorage('directCustomer', false)[0];
  const { pristine, invalid, handleSubmit, form, values, onError } = props;

  let customerInputs = (
    <FlexRow>
      <FlexBaseCol margin={{ right: 'small' }}>
        <FlexRow>
          <InputField name="name" placeholder="Your name" validate={validation(isRequired)} />
        </FlexRow>
        <OnChange name="name">
          {value => {
            const [firstName, lastName] = splitNames(value);
            form.reset({
              ...values,
              'first name': firstName,
              'last name': lastName
            });
          }}
        </OnChange>
        <HiddenField name="first name" />
        <HiddenField name="last name" />
        <FlexRow>
          <InputField
            type="email"
            name="email"
            placeholder="you@company.com"
            validate={validation(isRequired, isEmail)}
          />
        </FlexRow>
      </FlexBaseCol>

      <FlexBaseCol margin={{ left: 'small' }}>
        <FlexRow>
          <InputField
            name="job title"
            placeholder="Your job title"
            validate={validation(isRequired)}
          />
        </FlexRow>
        <FlexRow>
          <InputField
            name="company"
            placeholder="Your company name"
            validate={validation(isRequired)}
          />
        </FlexRow>
      </FlexBaseCol>
    </FlexRow>
  );

  if (directCustomer) {
    customerInputs = <HiddenField name="email" />;
  }

  useEffect(() => {
    if (formErrored) {
      onError(true);
    }
  }, [formErrored, onError]);

  return (
    <form
      action={window.env.LEAD_FORM_ACTION}
      method={window.env.LEAD_FORM_METHOD}
      onSubmit={e => {
        e.preventDefault();
        handleSubmit();
        e.target.submit();
      }}
      style={{ width: '100%' }}
    >
      <Grid>
        <FlexRow>
          <FlexBaseCol>
            <SkillField
              name="skill"
              skills={skills}
              fetching={skillsFetchingState.fetching}
              validate={validation(isRequired)}
            />
          </FlexBaseCol>
        </FlexRow>

        <FlexRow margin={{ top: '20px' }}>
          <FlexBaseCol>
            <SelectField
              name="topics"
              options={topics}
              fetching={topicsFetchingState.fetching}
              validate={validation(isRequired)}
            />
          </FlexBaseCol>
        </FlexRow>

        {customerInputs}

        <FlexRow>
          <FlexBaseCol>
            <StyledButton type="submit" variant="secondary" disabled={pristine || invalid}>
              Find freelance creators
              <Icon type="angle-right" />
            </StyledButton>
          </FlexBaseCol>
        </FlexRow>
      </Grid>
    </form>
  );
};

IntakeFormRender.propTypes = {
  pristine: PropTypes.bool.isRequired,
  invalid: PropTypes.bool.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  form: PropTypes.shape({ reset: PropTypes.func }).isRequired,
  values: PropTypes.shape({}).isRequired,
  onError: PropTypes.func.isRequired
};

export default IntakeFormRender;
